import { CURRENT_SERIES_ID } from '@/config/app.config';
import { read } from './http';
import { MOCK_GAMEWEEK } from './mock/gameweek.mock';
import type { Gameweek, GameweekStatus } from '@/types';

/** What GET /football/series/:id/gameweeks/current returns. */
interface ApiGameweek {
  seriesId: number;
  gameweek: number;
  roundId: number;
  name: string;
  startingAt: string;
  endingAt: string;
  deadline: string;
  isCurrent: boolean;
  finished: boolean;
}

/**
 * Derive the lifecycle state the UI cares about.
 *
 * The API reports `finished` and a deadline rather than a status, so "locked"
 * is inferred from the deadline having passed — which is what actually decides
 * whether a squad can still be changed.
 */
function toStatus(gw: ApiGameweek): GameweekStatus {
  if (gw.finished) return 'settled';
  if (new Date(gw.deadline).getTime() <= Date.now()) return 'live';
  return 'open';
}

/** GET /api/v1/football/series/:seriesId/gameweeks/current */
export async function getCurrentGameweek(signal?: AbortSignal): Promise<Gameweek> {
  const gw = await read<ApiGameweek>(
    `/football/series/${CURRENT_SERIES_ID}/gameweeks/current`,
    () => {
      const mock = MOCK_GAMEWEEK();
      return {
        seriesId: CURRENT_SERIES_ID,
        gameweek: mock.id,
        roundId: mock.id,
        name: mock.competition,
        startingAt: mock.deadlineAt,
        endingAt: mock.deadlineAt,
        deadline: mock.deadlineAt,
        isCurrent: true,
        finished: false,
      };
    },
    signal,
  );

  return {
    id: gw.gameweek,
    competition: gw.name,
    status: toStatus(gw),
    deadlineAt: gw.deadline,
  };
}
